import React, { useCallback, useState } from 'react';

import { BaseModal } from '../../../../components/BaseModal';
import { ErrorCard } from '../../../../components/ErrorCard';

import { BuyerChatsTable } from './components/BuyerChatsTable/BuyerChatsTable';
import { InvalidChatsSection } from './components/InvalidChatsSection';
import { useGetBuyerChats } from './hooks/useGetBuyerChats';
import { useUpdateUserChats } from './hooks/useUpdateUserChats';
import { BuyerChatsModalProps } from './types';

export const BuyerChatsModal: React.FC<BuyerChatsModalProps> = ({
  buyerId,
  userId,
  userName,
  buyerName,
  onClose,
  isOpen,
}) => {
  const [selectedChats, setSelectedChats] = useState<Set<string>>(new Set());
  const { chats, invalidChats, loading, error, fetchMore } = useGetBuyerChats({
    buyerId,
    userId,
  });
  // Initialize selected chats with the ones where the user is already a member
  React.useEffect(() => {
    if (chats.length > 0) {
      setSelectedChats(
        new Set(
          chats.filter((chat) => chat.isUserInChat).map((chat) => chat.id)
        )
      );
    }
  }, [chats]);

  const {
    addUserToChats,
    removeUserFromChats,
    loading: updating,
  } = useUpdateUserChats({
    userId,
    onSuccess: onClose,
  });

  const handleSave = useCallback(async () => {
    // chatsToAdd: selected now AND was NOT a member before
    const chatsToAdd = Array.from(selectedChats).filter(
      (chatId) => !chats.find((chat) => chat.id === chatId)?.isUserInChat
    );
    // chatsToRemove: was a member before AND is NOT selected now
    const chatsToRemove = chats
      .filter((chat) => chat.isUserInChat && !selectedChats.has(chat.id))
      .map((chat) => chat.id);

    if (chatsToAdd.length > 0) {
      await addUserToChats(chatsToAdd);
    }
    if (chatsToRemove.length > 0) {
      await removeUserFromChats(chatsToRemove);
    }
  }, [selectedChats, chats, addUserToChats, removeUserFromChats]);

  if (error) {
    return (
      <BaseModal isOpen={isOpen} onClose={onClose} title="Error Loading Chats">
        <ErrorCard
          title="Error Loading Chats"
          message="Something went wrong while loading the chats. Please try again."
          onRetry={() => window.location.reload()}
        />
      </BaseModal>
    );
  }

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add "${userName}" to Chats at "${buyerName}"`}
      footerContent={{
        onCancel: onClose,
        onConfirm: handleSave,
        isLoading: updating,
        isDisabled: selectedChats.size === 0,
        confirmText: 'Save',
      }}
    >
      <div className="max-h-[40vh] overflow-y-auto">
        <BuyerChatsTable
          chats={chats}
          loading={loading}
          userId={userId}
          selectedChats={selectedChats}
          onSelectChats={setSelectedChats}
          onScroll={(e) => {
            const target = e.target as HTMLDivElement;
            if (
              target.scrollHeight - target.scrollTop ===
              target.clientHeight
            ) {
              fetchMore();
            }
          }}
        />
        {invalidChats.length > 0 && (
          <InvalidChatsSection invalidChats={invalidChats} />
        )}
      </div>
    </BaseModal>
  );
};
